import React, { useEffect, useRef } from "react";

/**
 * UsdcToken — a tumbling stablecoin "note" rendered as two back-to-back
 * faces. Replaces the earlier DollarBill so the hero stops implying fiat.
 *
 * Visual language:
 *   • USDC blue gradient (#2775CA official + a lighter tint for the sheen)
 *   • "1 USDC" as the denomination
 *   • A circular USDC mark in the center (concentric ring + "$" glyph)
 *   • Back face keeps the deterministic QR-style grid so the piece still
 *     reads as "crypto receipt", not "fiat bill".
 *
 * Same tumble animation contract as DollarBill (l2-bill-loop + l2-bill-spin
 * classes) — we do NOT need to touch the CSS to swap the artwork.
 */
const USDC_BLUE = "#2775CA";
const USDC_BLUE_LIGHT = "#5AA0E8";
const USDC_BLUE_DEEP = "#1A4E80";

const UsdcToken = () => {
  const orbitRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isMobile || reduce) return;

    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        if (orbitRef.current) {
          const y = window.scrollY;
          const extra = (y * 0.25) % 360;
          orbitRef.current.style.setProperty("--bill-scroll-rot", `${extra}deg`);
        }
        raf = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const faceBase = {
    position: "absolute",
    inset: 0,
    borderRadius: "8px",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    color: "var(--l2-paper)",
    overflow: "hidden",
  };

  return (
    <div
      ref={orbitRef}
      data-testid="usdc-token"
      className="l2-bill-loop"
      style={{
        width: "var(--bill-w)",
        height: "var(--bill-h)",
        transformStyle: "preserve-3d",
        position: "relative",
        ["--bill-w"]: "clamp(140px, 16vw, 220px)",
        ["--bill-h"]: "clamp(62px, 7.5vw, 104px)",
      }}
    >
      {/* Spinning inner — continuous Y-tumble independent of orbit */}
      <div
        className="l2-bill-spin"
        style={{
          position: "absolute",
          inset: 0,
          transformStyle: "preserve-3d",
        }}
      >
        {/* ── FRONT FACE ───────────────────────────────────── */}
        <div
          style={{
            ...faceBase,
            transform: "translateZ(1px)",
            background: `linear-gradient(135deg, ${USDC_BLUE_LIGHT} 0%, ${USDC_BLUE} 45%, ${USDC_BLUE_DEEP} 100%)`,
            boxShadow:
              "inset 0 0 0 2px rgba(255,255,255,0.28)," +
              "inset 0 -8px 18px rgba(0,0,0,0.22)," +
              "0 1px 0 rgba(0,0,0,0.28)," +
              "0 -1px 0 rgba(255,255,255,0.20)",
            padding: "10px 12px",
          }}
        >
          {/* Engraved border frame */}
          <div
            style={{
              position: "absolute",
              inset: "6px",
              borderRadius: "5px",
              border: "1.5px dashed rgba(255, 255, 255, 0.42)",
              pointerEvents: "none",
            }}
          />

          <div className="flex items-center justify-between relative">
            <div
              className="l2-display drop-shadow-sm"
              style={{ fontSize: "clamp(11px, 1.35vw, 16px)", letterSpacing: "0.02em" }}
            >
              1 USDC
            </div>
            <div className="l2-mono text-[8px] md:text-[10px] opacity-80">
              BASE · 2026
            </div>
          </div>

          {/* USDC mark — concentric white ring with a $ glyph */}
          <div className="flex items-center justify-center mt-1 md:mt-2 relative">
            <div
              className="rounded-full flex items-center justify-center"
              style={{
                width: "clamp(28px, 3.2vw, 40px)",
                height: "clamp(28px, 3.2vw, 40px)",
                background:
                  "radial-gradient(circle at 35% 30%, rgba(255,255,255,1) 0%, rgba(230, 240, 250, 0.95) 60%, rgba(190,210,235,0.9) 100%)",
                border: "2.5px solid rgba(255, 255, 255, 0.95)",
                boxShadow:
                  "0 4px 10px rgba(0,0,0,0.28)," +
                  "inset 0 -2px 4px rgba(0,0,0,0.16)",
              }}
            >
              <div
                className="l2-display"
                style={{
                  fontSize: "clamp(13px, 1.55vw, 20px)",
                  color: USDC_BLUE,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                $
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-1 relative">
            <div className="l2-mono text-[8px] md:text-[10px] opacity-80">
              USDC
            </div>
            <div
              className="l2-display drop-shadow-sm"
              style={{ fontSize: "clamp(11px, 1.35vw, 16px)", letterSpacing: "0.02em" }}
            >
              1 USDC
            </div>
          </div>
        </div>

        {/* ── BACK FACE ────────────────────────────────────── */}
        <div
          style={{
            ...faceBase,
            transform: "rotateY(180deg) translateZ(1px)",
            background: `linear-gradient(135deg, ${USDC_BLUE_DEEP} 0%, ${USDC_BLUE} 50%, ${USDC_BLUE_LIGHT} 100%)`,
            boxShadow:
              "inset 0 0 0 2px rgba(255,255,255,0.22)," +
              "inset 0 8px 18px rgba(0,0,0,0.20)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: "6px",
              borderRadius: "5px",
              border: "1.5px dashed rgba(255, 255, 255, 0.42)",
              pointerEvents: "none",
            }}
          />
          {/* QR-style grid — same deterministic pattern as before */}
          <div
            className="grid gap-px"
            style={{
              gridTemplateColumns: "repeat(7, 1fr)",
              width: "58%",
              height: "62%",
            }}
          >
            {Array.from({ length: 49 }).map((_, i) => {
              const on = ((i * 13 + 7) % 5) < 3;
              return (
                <div
                  key={`usdc-qr-${i}`}
                  style={{
                    background: on ? "rgba(255, 255, 255, 0.95)" : "transparent",
                    borderRadius: "1px",
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Soft contact shadow under the token */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: "8%",
          right: "8%",
          bottom: "-22px",
          height: "18px",
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse, rgba(31, 27, 23, 0.28), transparent 70%)",
          filter: "blur(10px)",
          transform: "translateZ(-40px)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

export default UsdcToken;
